import { css } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import type { VideoFitType } from '../types/video';
import type { TRBLNumberCOORDS } from '../types/layout';
import {
  getBorderRadiusStyle,
  getInnerBorderRadiusStyle,
  getMediaQueryHeader,
} from './styleHelpers';

/**
 * Props interface for background video styling functions.
 * Works with both ContainerProps and PageProps.
 */
export interface BackgroundVideoStyleProps {
  bgVideoMode?: VideoFitType;
  bgVideoOpacity?: number;
}

export interface BackgroundVideoContainerStyleProps {
  borderRadius?: TRBLNumberCOORDS;
  borderWidth?: number;
  mobile?: { borderRadius?: TRBLNumberCOORDS; borderWidth?: number };
  tablet?: { borderRadius?: TRBLNumberCOORDS; borderWidth?: number };
}

/**
 * Get container style for background video wrapper.
 */
export const getBackgroundVideoContainerStyle = (
  props: BackgroundVideoContainerStyleProps,
  isServerRender: boolean,
) => {
  const { borderRadius, borderWidth, mobile, tablet } = props;

  // Calculate clip-path with inner radius when border is present
  const borderWidthValue = borderWidth || 0;
  const clipPathRadiusString =
    borderRadius && borderWidthValue
      ? getInnerBorderRadiusStyle(borderRadius, borderWidthValue).borderRadius
      : borderRadius
        ? getBorderRadiusStyle(borderRadius).borderRadius
        : undefined;

  const finalStyle: CSSInterpolation = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
    clipPath: clipPathRadiusString ? `inset(-0.5px round ${clipPathRadiusString})` : undefined,
  };

  if (mobile?.borderRadius || borderRadius) {
    const mobileBorderRadius = mobile?.borderRadius || borderRadius;
    const mobileBorderWidth = mobile?.borderWidth ?? borderWidthValue;
    const mobileClipPathRadius =
      mobileBorderRadius && mobileBorderWidth
        ? getInnerBorderRadiusStyle(mobileBorderRadius, mobileBorderWidth).borderRadius
        : mobileBorderRadius
          ? getBorderRadiusStyle(mobileBorderRadius).borderRadius
          : undefined;
    if (mobileClipPathRadius) {
      finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = {
        clipPath: `inset(-0.5px round ${mobileClipPathRadius})`,
      } as CSSObject;
    }
  }
  if (tablet?.borderRadius || borderRadius) {
    const tabletBorderRadius = tablet?.borderRadius || borderRadius;
    const tabletBorderWidth = tablet?.borderWidth ?? borderWidthValue;
    const tabletClipPathRadius =
      tabletBorderRadius && tabletBorderWidth
        ? getInnerBorderRadiusStyle(tabletBorderRadius, tabletBorderWidth).borderRadius
        : tabletBorderRadius
          ? getBorderRadiusStyle(tabletBorderRadius).borderRadius
          : undefined;
    if (tabletClipPathRadius) {
      finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = {
        clipPath: `inset(-0.5px round ${tabletClipPathRadius})`,
      } as CSSObject;
    }
  }

  return css(finalStyle);
};

/**
 * Get thumbnail style for background video.
 * Shows thumbnail image as placeholder before video loads.
 */
export const getBackgroundVideoThumbnailStyle = (
  props: BackgroundVideoStyleProps,
  thumbnailUrl: string | null | undefined,
) => {
  const { bgVideoMode, bgVideoOpacity } = props;

  if (thumbnailUrl) {
    return css({
      position: 'absolute',
      inset: 0,
      backgroundImage: `url(${thumbnailUrl})`,
      backgroundSize: bgVideoMode === 'fit' ? 'contain' : 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#000',
      opacity: (bgVideoOpacity ?? 100) / 100,
    });
  }

  return css({
    position: 'absolute',
    inset: 0,
    backgroundColor: '#000',
    opacity: (bgVideoOpacity ?? 100) / 100,
  });
};

/**
 * Get iframe style for background video.
 * Centers video and applies fill/fit mode.
 */
export const getBackgroundVideoIframeStyle = (props: BackgroundVideoStyleProps) => {
  const { bgVideoMode, bgVideoOpacity } = props;

  return css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '100%',
    minHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: bgVideoMode === 'fit' ? 'contain' : 'cover',
    objectPosition: 'center center',
    opacity: (bgVideoOpacity ?? 100) / 100,
    border: 'none',
  });
};
